import {
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Divider, Input, Layout, Row, Table, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import fileDownload from 'js-file-download';
import type { RangeValue } from 'rc-picker/es/interface';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { ModalCheckBoxGroup } from 'app/components/commons/ModalCheckBoxGroup/ModalCheckBoxGroup';
import { RangePicker } from 'app/components/fields/dayjsPickers';
import { StatusCell } from 'app/components/lists/cells';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import { useGetBookingsList } from 'app/hooks/data/extranet/bookings/useBooking';
import { useBookingsCsvDownload } from 'app/hooks/data/extranet/bookings/useBookingsCsvDownload';
import { ExtranetBookingStatus } from 'app/redux/models/ExtranetBooking/ExtranetBooking';
import type { PAX } from 'app/typings/PAX';
import type { BookingListItem } from 'app/typings/extranetBookings/booking';
import { VccStatus } from 'app/typings/vcc';
import { dayjs, differenceInCalendarDays } from 'app/utils/dayjs';
import type { Dayjs } from 'app/utils/dayjs';
import { useDebounce } from 'app/utils/hooks/useDebounce';
import { useSearchParams } from 'app/utils/searchParams';
import { formatSortingForAPI } from 'app/utils/sort';
import { parseUppercaseSnakeTextToCapitalize } from 'app/utils/string';
import { toSnakeCase } from 'app/utils/strings';
import { amplitudeTrack, useTrackViewPage } from 'app/utils/track/amplitude';
import { getVCCStatusDisplayInfo } from 'app/utils/vcc';

import 'styles/pages/Extranet/extranet-table.scss';

import { ExtranetHeader } from '../../commons/ExtranetHeader/ExtranetHeader';
import { ModalCheckBox } from '../ModalCheckBox';

import { BookingDetail } from './BookingDetail';
import './ExtranetLegacyBookings.scss';
import { getTooltipText } from './utils';

const { Content } = Layout;
const { Column } = Table;

declare type SortOrder = 'descend' | 'ascend' | null;

const PAGE = 'Bookings';

const formatAdults = (adults: number) =>
  `${adults} ${adults === 1 ? 'adult' : 'adults'}`;
const formatChildren = (children: number) =>
  `${children} ${children === 1 ? 'child' : 'children'}`;
const formatBabies = (babies: number) =>
  `${babies} ${babies === 1 ? 'baby' : 'babies'}`;
const formatPax = (customer: PAX) =>
  `${formatAdults(customer.adults)}, ${formatChildren(
    customer.children
  )}, ${formatBabies(customer.babies)}`;
const formatDate = (date: string) => dayjs(date).format('DD/MM/YYYY');

const sortingIntitialState = {
  fieldName: 'checkin',
  sortOrder: 'DESC',
};
const BOOKING_CSV_FILE_NAME = 'bookings.csv';

export const ExtranetLegacyBookings = () => {
  const { hotelId } = useContext(HotelContext);
  const { packages } = useContext(LegacyHotelContext);

  const location = useLocation();
  const { setSearchParams, clearSearchParam } = useSearchParams();

  const { setPage } = useContext(TaskCenterContext);

  useEffect(() => {
    setPage(PAGE);
  });

  const { currencyFormatter } = useContext(CurrencyContext);
  const [bookingCode, setBookingCode] = useState<string | undefined>(undefined);
  const [scrollVCC, setScrollVCC] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const [sortQueryParams, setSortQueryParams] = useState<
    | {
        fieldName: string;
        sortOrder: string;
      }
    | undefined
  >(sortingIntitialState);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [pageNumber, setPageNumber] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ExtranetBookingStatus[]>([
    ExtranetBookingStatus.Accepted,
    ExtranetBookingStatus.Cancelled,
  ]);

  const defaultVccStatusFilters = [
    ...Object.values(VccStatus).map((value) => value),
    'BANK_TRANSFER',
  ];

  const [vccStatusFilter, setVccStatusFilter] = useState(
    defaultVccStatusFilters
  );

  const [packageFilter, setPackageFilter] = useState<number[]>(
    packages.map((pkg) => pkg.id) ?? []
  );

  const formatSorting = useCallback(
    () =>
      sortQueryParams
        ? `${sortQueryParams.fieldName}:${sortQueryParams.sortOrder}`
        : undefined,
    [sortQueryParams]
  );

  useEffect(() => {
    if (!!packages.length) {
      setPackageFilter(packages.map((pkg) => pkg.id));
    }
  }, [packages]);

  useEffect(() => {
    if (document.readyState === 'complete') {
      const query = new URLSearchParams(location.search);
      const valueVCC = query.get('bookingVCC');
      const valueBooking = query.get('booking');

      if (valueVCC) {
        setBookingCode(valueVCC);
        setScrollVCC(true);

        const element = document.getElementById('virtual-card');

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      if (valueBooking) {
        setBookingCode(valueVCC ?? valueBooking);
      }
    }
  }, [location.search]);

  const formatVccStatus = useCallback(() => {
    const vccStatusWithoutVCC = vccStatusFilter.filter(
      (value) => value !== 'VCC'
    );

    if (vccStatusWithoutVCC.length === defaultVccStatusFilters.length) {
      return [];
    }

    return vccStatusWithoutVCC;
  }, [defaultVccStatusFilters.length, vccStatusFilter]);

  const debouncedSearch = useDebounce(search);

  const makePayload = useCallback(
    () => ({
      hotelId: hotelId ?? 0,
      offset: (pageNumber - 1) * 50,
      search: debouncedSearch || undefined,
      sorting: formatSorting() || undefined,
      beginning: startDate,
      ending: endDate,
      status: statusFilter,
      vccStatus: formatVccStatus(),
      packageIds: packageFilter,
      isOld: false,
    }),
    [
      debouncedSearch,
      endDate,
      formatSorting,
      formatVccStatus,
      hotelId,
      pageNumber,
      startDate,
      statusFilter,
      packageFilter,
    ]
  );

  const { data: bookingsData, isLoading: bookingsLoading } = useGetBookingsList(
    makePayload(),
    hotelId
  );

  useEffect(() => {
    setPageNumber(1);
  }, [
    setPageNumber,
    hotelId,
    search,
    startDate,
    endDate,
    statusFilter,
    vccStatusFilter,
    packageFilter,
    sortQueryParams,
  ]);

  useTrackViewPage({ page: 'bookings' });

  const handleFilterDate = (interval: RangeValue<Dayjs>) => {
    if (!interval || !interval[0] || !interval[1]) {
      setStartDate(undefined);
      setEndDate(undefined);
    } else {
      setStartDate(interval[0].format('YYYY-MM-DD'));
      setEndDate(interval[1].format('YYYY-MM-DD'));
    }

    setPageNumber(1);
  };

  const handleSorting = (field: string, sort: SortOrder) => {
    const newSortQueryParams = sort
      ? {
          fieldName: toSnakeCase(field),
          sortOrder: formatSortingForAPI(sort),
        }
      : undefined;

    setSortQueryParams(newSortQueryParams);
    setPageNumber(1);
  };

  const { mutate: downloadBookingsCsv } = useBookingsCsvDownload();

  const downloadCSV = () => {
    amplitudeTrack('Download Booking CSV');

    const payload = makePayload();

    downloadBookingsCsv(payload, {
      onSuccess: (data) => {
        fileDownload(data, BOOKING_CSV_FILE_NAME);
      },
      onError: (error) => {
        console.error('Failed to download CSV:', error);
      },
    });
  };

  const handleStatusFilter = (statuses: ExtranetBookingStatus[]) => {
    if (!statuses.length) {
      setStatusFilter([
        ExtranetBookingStatus.Accepted,
        ExtranetBookingStatus.Cancelled,
      ]);
    } else {
      setStatusFilter(statuses);
    }

    setPageNumber(1);
  };

  const handlePackageFilter = (pkgs: number[]) => {
    if (!pkgs.length) {
      setPackageFilter([]);
    } else {
      setPackageFilter(pkgs);
    }

    setPageNumber(1);
  };

  const pagination = (className: string) => (
    <div className={className}>
      <Button
        disabled={pageNumber === 1}
        onClick={() => setPageNumber(pageNumber - 1)}
      >
        <LeftOutlined />
      </Button>
      <Button>{pageNumber}</Button>
      <Button
        disabled={(bookingsData?.bookings?.length ?? 0) < 50}
        onClick={() => setPageNumber(pageNumber + 1)}
      >
        <RightOutlined />
      </Button>
    </div>
  );

  const header = () => (
    <div className="body-header">
      <div className="left">
        <Input
          placeholder="Search here..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          suffix={<SearchOutlined />}
          className="search"
          size="large"
        />
        <div className="checkin-input-title">Check-in period:</div>
        <RangePicker
          onChange={handleFilterDate}
          format="ddd DD MMM"
          ranges={{
            Today: [dayjs(), dayjs()],
            'This Week': [dayjs().startOf('isoWeek'), dayjs().endOf('isoWeek')],
          }}
          className="range-picker"
          size="large"
        />
      </div>
      <div className="right">
        <Button onClick={downloadCSV} className="button" size="large">
          Download .csv
        </Button>
        {pagination('button-wrapper')}
      </div>
    </div>
  );

  const handleRowActions = ({ code }: BookingListItem) => ({
    onClick: () => {
      setBookingCode(code);
      setSearchParams({ booking: code });
    },
  });

  const closeDrawer = () => {
    setBookingCode(undefined);
    clearSearchParam('booking');
    clearSearchParam('bookingVCC');
  };

  if (!hotelId) {
    return <></>;
  }

  return (
    <Layout className="extranet-table">
      <ExtranetHeader page={PAGE} />
      <Content className="body">
        <Table
          dataSource={bookingsData?.bookings ?? []}
          pagination={false}
          rowKey="code"
          onRow={handleRowActions}
          title={header}
          loading={bookingsLoading}
          onChange={(__, ___, sorter) => {
            // @ts-ignore -- legacy directive, needs investigation
            const { field, order } = sorter;

            handleSorting(field, order);
          }}
        >
          <Column
            title="Date"
            dataIndex="createdAt"
            render={formatDate}
            sorter
          />
          <Column
            title="Guest"
            render={({ customer, pax }) => (
              <div style={{ minWidth: 200 }}>
                <div>{`${customer.gender} ${customer.firstName} ${customer.lastName}`}</div>
                <div style={{ color: '#8C8C8C' }}>{formatPax(pax)}</div>
              </div>
            )}
          />
          <Column
            title="Check-in"
            dataIndex="checkin"
            render={formatDate}
            sorter
          />
          <Column title="Check-out" dataIndex="checkout" render={formatDate} />
          <Column
            title="Nights"
            dataIndex="nbNight"
            render={(_, record: BookingListItem) => {
              const nbNight = differenceInCalendarDays(
                new Date(record.checkout ?? ''),
                new Date(record.checkin ?? '')
              );

              return (
                <div
                  className={classNames([
                    { 'ug-multinight-bookings': nbNight > 1 },
                  ])}
                  nb-night={nbNight}
                >
                  {nbNight}
                </div>
              );
            }}
          />
          <Column
            title={
              !!packages.length && packages.length > 1 ? (
                <ModalCheckBox
                  title="Room & Package"
                  defaultValue={packages.map((pkg) => pkg.id)}
                  onChange={handlePackageFilter}
                  items={packages.map((pkg) => ({
                    value: pkg.id,
                    label:
                      pkg.aliasForHotel.trim() ||
                      `${pkg.coverLabel}`.trim() ||
                      `Package #${pkg.id}`,
                  }))}
                  value={packageFilter}
                  isCenter={false}
                />
              ) : (
                'Room & Package'
              )
            }
            dataIndex="roomName"
            render={(roomName, rowValues: BookingListItem) => (
              <div className="room-hotel-name">
                <div>{roomName ?? rowValues.roomCategory}</div>
                <div className="alias-for-hotel">{rowValues.aliasForHotel}</div>
              </div>
            )}
            className="sort-column"
          />
          <Column title="Extras" dataIndex="extrasCount" />
          <Column
            title="Total incl. VAT"
            dataIndex="totalAmount"
            render={(totalAmount) => `${currencyFormatter(totalAmount || 0)}`}
          />
          {bookingsData?.hasVccBookings && (
            <Column
              title={
                <ModalCheckBoxGroup
                  title="VCC Status"
                  isCenter={false}
                  onChange={(statuses) => {
                    setVccStatusFilter(statuses as VccStatus[]);
                    setPageNumber(1);
                  }}
                  items={[
                    {
                      title: 'Virtual Card Status',
                      children: [
                        ...Object.values(VccStatus).map((value) => ({
                          key: value,
                          title: parseUppercaseSnakeTextToCapitalize(value),
                        })),
                      ],
                      key: 'VCC',
                      selectable: false,
                    },
                    {
                      title: <Divider />,
                      key: 'divider',
                      className: 'divider-element',
                      disabled: true,
                      checkable: false,
                      isDivider: true,
                    },
                    { title: 'Bank transfer', key: 'BANK_TRANSFER' },
                  ]}
                  value={vccStatusFilter}
                />
              }
              dataIndex="code"
              render={(_, record: BookingListItem) => {
                if (record.issuingCardId) {
                  const status = getVCCStatusDisplayInfo(record.vccStatus);
                  const tooltipText = getTooltipText(status.status);

                  return (
                    <Row align="middle">
                      <Tag color={status.color}>{status.text}</Tag>
                      {!!tooltipText &&
                        status.status === VccStatus.ToRefund && (
                          <Tooltip title={tooltipText}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        )}
                    </Row>
                  );
                }

                return 'Bank transfer';
              }}
            />
          )}
          <Column title="Booking ID" dataIndex="code" />
          <Column
            title={
              <ModalCheckBox
                title="Status"
                defaultValue={[
                  ExtranetBookingStatus.Accepted,
                  ExtranetBookingStatus.Cancelled,
                ]}
                onChange={handleStatusFilter}
                items={[
                  {
                    value: ExtranetBookingStatus.Accepted,
                    label: 'Confirmed',
                  },
                  {
                    value: ExtranetBookingStatus.Cancelled,
                    label: 'Cancelled',
                  },
                ]}
                value={statusFilter}
              />
            }
            dataIndex="status"
            render={StatusCell}
            className="sort-column"
          />
        </Table>
        {pagination('footer')}
      </Content>
      <BookingDetail
        bookingCode={bookingCode}
        scrollVCC={scrollVCC}
        onClose={() => closeDrawer()}
      />
    </Layout>
  );
};
